import { useMemo } from 'react'

import { usePermissions } from '@/hooks/usePermissions'
import { useTooltipAnchor } from '@/hooks/useTooltipAnchor'
import { useTranslation } from 'react-i18next'
import { HiOutlineTrash } from 'react-icons/hi'

import {
  Box,
  TableMenuButton,
  Toggle,
  Tooltip,
  UserAvatar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { formatDateTime, formatTimeForTableCell } from '@/utils/formatDateTime'

import { RULES_TABLE_ACTION_COLUMN_WIDTH } from '../constants/layout'
import type { RuleRowData, RuleTableColumn } from '../types/rulesTable'
import { DarkTooltipText } from './DarkTooltipText'
import { RuleNameLink } from './RuleNameLink'

interface UseDarkRulesColumnsIProps {
  colorScheme: ColorScheme
  onArchiveRule: (rule: RuleRowData['active']) => void
  onToggleRule: (rule: RuleRowData['active']) => void
}

interface DarkRulesStatusCellIProps {
  colorScheme: ColorScheme
  row: RuleRowData
  onToggleRule: (rule: RuleRowData['active']) => void
}

const DarkRulesStatusCell = ({
  colorScheme,
  row,
  onToggleRule,
}: DarkRulesStatusCellIProps) => {
  const { t } = useTranslation()
  const { anchorEl, onMouseEnter, onMouseLeave } = useTooltipAnchor()
  const { shouldEnableEditIncidentRule } = usePermissions()

  const tooltipLabel = !shouldEnableEditIncidentRule
    ? t('automations:no_permission_edit_rule_tooltip')
    : row.active?.status
    ? t('common:active')
    : t('common:disabled')

  return (
    <Box
      as='span'
      display='inline-flex'
      // Keep clicks and middle-clicks on the control from reaching the row,
      // which would otherwise open the rule (in a new tab on middle-click).
      onAuxClick={(event: React.MouseEvent<HTMLElement>) =>
        event.stopPropagation()
      }
      onClick={(event: React.MouseEvent<HTMLElement>) =>
        event.stopPropagation()
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Toggle
        aria-label={tooltipLabel}
        colorScheme={colorScheme}
        data-testid={`rulesPage_table_activeToggleCell${row.id}`}
        disabled={!shouldEnableEditIncidentRule}
        isOn={!!row.active?.status}
        onChange={() => onToggleRule(row.active)}
      />
      <Tooltip
        anchorEl={anchorEl}
        colorScheme={colorScheme}
        label={tooltipLabel}
      />
    </Box>
  )
}

interface DarkRulesActionsCellIProps {
  colorScheme: ColorScheme
  row: RuleRowData
  onArchiveRule: (rule: RuleRowData['active']) => void
}

const DarkRulesActionsCell = ({
  colorScheme,
  row,
  onArchiveRule,
}: DarkRulesActionsCellIProps) => {
  const { t } = useTranslation()
  const { shouldEnableEditIncidentRule } = usePermissions()
  const isArchiveDisabled = !!row.active?.status

  if (!shouldEnableEditIncidentRule) return null

  return (
    <Box
      as='span'
      display='inline-flex'
      // Keep clicks and middle-clicks on the control from reaching the row,
      // which would otherwise open the rule (in a new tab on middle-click).
      onAuxClick={(event: React.MouseEvent<HTMLElement>) =>
        event.stopPropagation()
      }
      onClick={(event: React.MouseEvent<HTMLElement>) =>
        event.stopPropagation()
      }
    >
      <TableMenuButton
        colorScheme={colorScheme}
        items={[
          {
            color: 'interactive.danger',
            disabled: isArchiveDisabled,
            disabledTooltipLabel: isArchiveDisabled
              ? t('automations:col_rule_must_be_inactive')
              : undefined,
            icon: <HiOutlineTrash size={16} />,
            label: t('automations:table_archive_action'),
            onClick: () => onArchiveRule(row.active),
          },
        ]}
      />
    </Box>
  )
}

export const useDarkRulesColumns = ({
  colorScheme,
  onArchiveRule,
  onToggleRule,
}: UseDarkRulesColumnsIProps): RuleTableColumn[] => {
  const { t } = useTranslation()

  return useMemo(
    () => [
      {
        key: 'createdAt',
        label: t('automations:col_created'),
        // Fits the full 'HH:mm:ss | MM/dd/yyyy' timestamp without truncation
        width: '220px',
        sortColumn: 'createdAt',
        cell: (row) => {
          const value = row.createdAt ? formatDateTime(row.createdAt) : ''
          const label = row.createdAt
            ? formatTimeForTableCell(row.createdAt)
            : ''

          return (
            <DarkTooltipText
              colorScheme={colorScheme}
              dataTestId='rulesPage_table_createdCell'
              label={label}
            >
              {value}
            </DarkTooltipText>
          )
        },
      },
      {
        key: 'lastMatched',
        label: t('automations:col_last_matched'),
        // Same timestamp format as createdAt — same width to avoid truncation
        width: '220px',
        cell: (row) => {
          const value = row.lastMatched
            ? formatDateTime(row.lastMatched)
            : t('automations:recurrence_never_label')
          const label = row.lastMatched
            ? formatTimeForTableCell(row.lastMatched)
            : t('automations:recurrence_never_label')

          return (
            <DarkTooltipText
              colorScheme={colorScheme}
              dataTestId='rulesPage_table_lastMatchedCell'
              label={label}
            >
              {value}
            </DarkTooltipText>
          )
        },
      },
      {
        key: 'ruleName',
        flexGrow: 2,
        label: t('automations:col_rule_name'),
        maxWidth: '560px',
        minWidth: '220px',
        sortColumn: 'ruleName',
        cell: (row) => (
          <RuleNameLink id={row.id} isV2={row.isV2}>
            <DarkTooltipText
              colorScheme={colorScheme}
              dataTestId='rulesPage_table_nameCell'
              label={row.ruleName}
            >
              {row.ruleName}
            </DarkTooltipText>
          </RuleNameLink>
        ),
      },
      {
        key: 'createdBy',
        align: 'center',
        label: t('automations:col_created_by'),
        maxWidth: '220px',
        minWidth: '130px',
        cell: (row) => (
          <UserAvatar colorScheme={colorScheme} name={row.createdBy} />
        ),
      },
      {
        key: 'action',
        flexGrow: 1,
        label: t('automations:col_action'),
        maxWidth: '456px',
        minWidth: '170px',
        cell: (row) => (
          <DarkTooltipText colorScheme={colorScheme} label={row.action}>
            {row.action}
          </DarkTooltipText>
        ),
      },
      {
        key: 'active',
        align: 'center',
        label: t('automations:col_active'),
        width: '110px',
        cell: (row) => (
          <DarkRulesStatusCell
            colorScheme={colorScheme}
            onToggleRule={onToggleRule}
            row={row}
          />
        ),
      },
      {
        key: 'actions',
        align: 'center',
        width: RULES_TABLE_ACTION_COLUMN_WIDTH,
        cell: (row) => (
          <DarkRulesActionsCell
            colorScheme={colorScheme}
            onArchiveRule={onArchiveRule}
            row={row}
          />
        ),
      },
    ],
    [colorScheme, onArchiveRule, onToggleRule, t]
  )
}
